import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import swal from 'sweetalert';
import Autocomplete from '@mui/material/Autocomplete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import Client from './Client';
import Config from './ClientConfig';
import DropDown from './DropDown';

import noImage from './../images/noImage.png';

// Endpoints
const API_URL_PRODUCTS = Config.API_URL_PRODUCT + 'list_products/';
const API_URL_COMBOS   = Config.API_URL_COMBO   + 'list_combos/';
const API_URL_DIRECT   = Config.API_URL_PROMOTION_DIRECT;
const LIST_CACHE = new Map(); // product/combo list cache, keyed by endpoint + real filters
const LIST_CACHE_TTL_MS = 60 * 1000;
function clearPromotionListCache() {
  LIST_CACHE.clear();
}

export default class DirectPromotion extends React.Component {
  constructor(props) {
    super(props);
    const today = this.toBusinessDate(new Date().toISOString()).slice(0, 10);
    this.state = {
      client: new Client('none'),
      edit: !!props.isNew,
      allowEdit: props.allowEdit !== false,
      expanded: !!props.isNew,                // collapsed by default (also for new/edit)
      isSaving: false,
      somethingHasChanged: !!props.isNew,
      tabValue: 'details',

      item: {
        iref: '',
        name: '',
        dateFrom: props.isNew ? `${today}T00:00:00` : '',
        dateTo: props.isNew ? `${today}T23:59:59` : '',
        client: null,                  // string iref
        product: null,                 // string iref
        combo: null,                   // string iref
        status: 0,                     // Registered
        qrCode: '',                    // kept, not shown
        claimedOn: null,
        consumedOn: null,
        image: null,
      },

      productOptsAll: [],
      productOpts: [],
      productObj: null,
      productSearching: false,

      comboOptsAll: [],
      comboOpts: [],
      comboObj: null,
      comboSearching: false,

      clientQuery: '',
      clientResolved: null,
    };
  }

  // Compact props for inputs
  compactTF = { size: 'small', margin: 'dense', variant: 'outlined' };

  CLAIM_STATUS = { 0:'Registered', 1:'Claimed', 2:'Consumed', 3:'Missed', 4:'Pending' };
  statusName = (v) => this.CLAIM_STATUS[Number(v)] ?? String(v);

  // ---- date helpers (aligned with Promotion) ----
  toBusinessDate(value) {
    const s = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}[ T].*(?:Z|[+-]\d{2}:?\d{2})$/i.test(s)) return s;
    const instant = new Date(s.replace(' ', 'T'));
    if (Number.isNaN(instant.getTime())) return '';
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Bucharest',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(instant).map(({ type, value: part }) => [type, part]));
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
  }

  toPayloadDate(raw, { endOfDay = false, preserveOffset = false } = {}) {
    if (!raw) return null;
    const s = this.toBusinessDate(raw);
    // An untouched API timestamp retains its exact instant, including at DST overlaps.
    const original = String(raw).trim();
    if (preserveOffset && s && /^\d{4}-\d{2}-\d{2}[ T].*(?:Z|[+-]\d{2}:?\d{2})$/i.test(original)) {
      return original.replace(' ', 'T');
    }
    // Date-only To values include the whole day; preserve explicit times.
    const time = (match) => match[4] === undefined
      ? (endOfDay ? '23:59:59' : '00:00:00')
      : `${match[4]}:${match[5]}:${match[6] || '00'}`;

    // YYYY-MM-DD or YYYY-MM-DD[ T]HH:mm[:ss][.sss][Z]
    let m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
    if (m) return `${m[1]}-${m[2]}-${m[3]} ${time(m)}`;

    // DD-MM-YYYY or DD-MM-YYYY[ T]HH:mm[:ss]
    m = /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
    if (m) return `${m[3]}-${m[2]}-${m[1]} ${time(m)}`;

    return null;
  }
  toDMY(value) {
    if (!value) return '';
    const s = this.toBusinessDate(value);

    let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s); // YYYY-MM-DD -> DD-MM-YYYY
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;

    m = /^(\d{2})-(\d{2})-(\d{4})/.exec(s); // already DMY
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;

    return '';
  }

  toDateTimeInput = (value, endOfDay = false) => (
    this.toPayloadDate(value, { endOfDay })?.slice(0, 16).replace(' ', 'T') || ''
  );

  scheduleLabel = (value, endOfDay = false) => (
    value ? `${this.toDMY(value)} ${this.toPayloadDate(value, { endOfDay })?.slice(11, 16) || ''}` : '—'
  );

  changeSchedule = (field, value) => {
    this.setState((state) => {
      const previous = state.item[field];
      let next = value;
      if (next) {
        const oldValue = this.toDateTimeInput(previous, field === 'dateTo');
        const sameTime = oldValue && oldValue.slice(11) === next.slice(11);
        const seconds = sameTime
          ? this.toPayloadDate(previous, { endOfDay: field === 'dateTo' }).slice(17, 19)
          : field === 'dateTo' && next.endsWith('T23:59') ? '59' : '00';
        next = next === oldValue ? previous : `${next}:${seconds}`;
      }
      return {
        item: { ...state.item, [field]: next },
        somethingHasChanged: true,
      };
    });
  };

  // ---- error helpers (jQuery pushData compatible) ----
  isErrorLike = (data) => {
    if (!data) return true;
    if (typeof data === 'string') return true;
    if (data.detail && typeof data.detail === 'string') return true;
    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) return true;
    for (const k of Object.keys(data)) {
      const v = data[k];
      if (Array.isArray(v) && v.length && v.every(x => typeof x === 'string')) return true;
    }
    return false;
  };
  normalizeErrors = (data) => {
    if (!data) return 'Unknown error';
    if (typeof data === 'string') return data;
    if (data.detail) return String(data.detail);
    if (Array.isArray(data.non_field_errors)) return data.non_field_errors.join('\n');
    const parts = [];
    Object.keys(data).forEach(k => {
      const v = data[k];
      if (Array.isArray(v)) parts.push(`${k}: ${v.join(', ')}`);
      else if (typeof v === 'string') parts.push(`${k}: ${v}`);
      else parts.push(`${k}: ${JSON.stringify(v)}`);
    });
    return parts.length ? parts.join('\n') : 'Unknown error';
  };

  // Valid when both dates exist and From <= To
  hasValidDates = () => {
    const { dateFrom, dateTo } = this.state.item || {};
    if (!dateFrom || !dateTo) return false;
    const from = this.toPayloadDate(dateFrom, { endOfDay: false });
    const to   = this.toPayloadDate(dateTo,   { endOfDay: true  });
    if (!from || !to) return false;
    return from <= to;
  };

  // For inline field errors
  getDateErrors = () => {
    const { dateFrom, dateTo } = this.state.item || {};
    const missingFrom = !dateFrom;
    const missingTo   = !dateTo;
    if (missingFrom || missingTo) return { from: missingFrom, to: missingTo, range: false };

    const from = this.toPayloadDate(dateFrom, { endOfDay: false });
    const to   = this.toPayloadDate(dateTo,   { endOfDay: true  });
    const badRange = !from || !to || from > to;
    return { from: false, to: false, range: badRange };
  };

  // --- client search helpers ---
  normalizeContact = (s) => {
    const x = String(s || '').trim();
    if (!x) return null;
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x);
    if (isEmail) return { email: x };
    const digits = x.replace(/[^\d+]/g, '');
    if (digits.length >= 7) return { phone: digits };
    return null;
  };

  findClientByContact = () => {
    const contact = this.normalizeContact(this.state.clientQuery);
    if (!contact) { swal('Invalid input','Type an email or phone (min 7 digits).','warning'); return; }

    // Query backend with exact email OR exact phone
    const data = this.state.client.getData(Config.API_URL_CLIENT, contact);

    // Accept array, {clients:[]}, {results:[]}, or {client:{...}}
    const list = Array.isArray(data) ? data
      : (Array.isArray(data?.clients) ? data.clients
      : (Array.isArray(data?.results) ? data.results
      : (data?.client ? [data.client] : [])));

    // Exact-match on the same field we sent
    const key = contact.email ? 'email' : 'phone';
    const val = contact[key];
    const matches = list.filter(c =>
      c && c.user && c.user.person && String(c.user.person[key] || '').trim() === val
    );

    if (matches.length === 1) {
      const row = matches[0];
      const iref = String(row.iref || row.id || '');
      if (!iref) { swal('Not found','Client record missing iref.','warning'); return; }
      this.setState(s => ({
        item: { ...s.item, client: iref },
        clientResolved: { iref, [key]: val },
        somethingHasChanged: true,
      }));
      swal('Client found', `Linked client #${iref}`, 'success', { buttons:false, timer:900 });
    } else if (matches.length > 1) {
      swal('Multiple matches', 'Found more than one client for that contact. Please refine.', 'warning');
    } else {
      swal('Not found', 'No client with that email/phone.', 'warning');
    }
  };

  // ---- lifecycle ----
  componentDidMount() {
    if (this.props.item) {
      const i = { ...this.props.item };
      this.setState({ item: i }, () => this.primeSelections());
    } else {
      this.primeSelections();
    }
  }
  componentDidUpdate(prevProps) {
    if (this.props !== prevProps && this.props.item) {
      this.setState({ item: { ...this.props.item } }, () => this.primeSelections());
    }
    if (this.props.isNew && !prevProps.isNew && !this.state.expanded) {
      this.setState({ expanded: true });
    }
  }

  // ---- async lists ----
  async fetchList(endpoint, q) {
    try {
      const qp = (q || '').trim();

      const queryData = {};

      if (qp && qp.length >= 2) {
        queryData.search = qp;
      }

      if (endpoint === API_URL_PRODUCTS) {
        queryData.status = 2;
        queryData.isForSale = 1;
        queryData.isSoldAlone = 1;
        queryData.isSellable = 1;
        queryData.available_for_promotion = 1;
        queryData.orderBy_name = true;
      }

      if (endpoint === API_URL_COMBOS) {
        queryData.status = 2;
        queryData.isForSale = 1;
        queryData.isSellable = 1;
        queryData.available_for_promotion = 1;
        queryData.orderBy_name = true;
      }

      const sortedQueryData = Object.keys(queryData)
        .sort()
        .reduce((acc, key) => {
          acc[key] = queryData[key];
          return acc;
        }, {});

      const cacheKey = endpoint + '|' + JSON.stringify(sortedQueryData);
      const cached = LIST_CACHE.get(cacheKey);
        if (cached && Date.now() - cached.createdAt < LIST_CACHE_TTL_MS) {
          return cached.data;
        }

      const data = this.state.client.getData(endpoint, queryData);

      const list = Array.isArray(data) ? data
        : (Array.isArray(data?.results) ? data.results
        : (Array.isArray(data?.products) ? data.products
        : (Array.isArray(data?.productCombos) ? data.productCombos
        : (Array.isArray(data?.combos) ? data.combos : []))));

      const mapped = list.map(x => ({
        iref: String(x.iref ?? x.id ?? ''),
        name: String(x.name ?? x.productName ?? x.comboName ?? '').trim()
      })).filter(x => x.iref);

      mapped.sort((a,b)=>a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

      LIST_CACHE.set(cacheKey, {
        createdAt: Date.now(),
        data: mapped,
      });
      return mapped;

    } catch (e) {
      return [];
    }
  }

  primeSelections = async () => {
    try {
      const [prods, combos] = await Promise.all([
        this.fetchList(API_URL_PRODUCTS, ''),
        this.fetchList(API_URL_COMBOS, ''),
      ]);
      const pId = this.state.item?.product ? String(this.state.item.product) : null;
      const cId = this.state.item?.combo ? String(this.state.item.combo) : null;
      this.setState({
        productOptsAll: prods || [],
        productOpts: prods || [],
        productObj: pId ? ((prods||[]).find(o=>String(o.iref)===pId) || null) : null,
        comboOptsAll: combos || [],
        comboOpts: combos || [],
        comboObj:   cId ? ((combos||[]).find(o=>String(o.iref)===cId) || null) : null,
      });
    } catch {}
  };

  onProductInput = (_e, value, reason) => {
    if (reason && reason !== 'input') return;
    const q = (value || '').trim().toLowerCase();
    const all = this.state.productOptsAll || [];
    if (!q) { this.setState({ productOpts: all, productSearching: false }); return; }
    const filtered = all.filter(o => (o.name || '').toLowerCase().includes(q));
    this.setState({ productOpts: filtered, productSearching: false });
  };
  onComboInput = (_e, value, reason) => {
    if (reason && reason !== 'input') return;
    const q = (value || '').trim().toLowerCase();
    const all = this.state.comboOptsAll || [];
    if (!q) { this.setState({ comboOpts: all, comboSearching: false }); return; }
    const filtered = all.filter(o => (o.name || '').toLowerCase().includes(q));
    this.setState({ comboOpts: filtered, comboSearching: false });
  };

  // ---- UI handlers ----
  handleEditClick = () => {
    if (!this.state.allowEdit) return;
    this.setState(prev => {
      const nextEdit = !prev.edit;
      return {
        edit: nextEdit,
        expanded: nextEdit ? true : prev.expanded, // open when entering edit
      };
    });
  };
  handleTabChange = (_, tab) => this.setState({ tabValue: tab });
  changeValue = (e) => {
    const id = e.target.id;
    const value = e.target.value;
    this.setState(s => ({ item: { ...s.item, [id]: value }, somethingHasChanged: true }));
  };

  // ---- Save ----
  save = async () => {
    const src = this.state.item;
    if (!src.client) { swal('Missing client', 'Client is required.', 'warning'); return; }
    if (!this.hasValidDates()) {
      swal('Missing or invalid period', 'Please set both From and To (From must be before or equal to To).', 'warning');
      return;
    }
    if (src.product && src.combo) {
      swal('Invalid scope', 'Select either a Product or a Combo, not both.', 'warning');
      return;
    }
    if (!src.product && !src.combo) {
      swal('Missing scope', 'Select a Product or a Combo.', 'warning');
      return;
    }
    if (!(this.state.edit && this.state.somethingHasChanged)) { swal('Error', 'Nothing to save', 'error'); return; }

    this.setState({ isSaving: true });
    const payload = {
      ...src,
      // If user leaves time empty: From -> 00:00:00, To -> 23:59:59
      dateFrom: this.toPayloadDate(src.dateFrom, { endOfDay: false, preserveOffset: true }),
      dateTo:   this.toPayloadDate(src.dateTo,   { endOfDay: true, preserveOffset: true }),
      status: Number.isFinite(+src.status) ? parseInt(src.status, 10) : 0,
      client:  src.client ? String(src.client).trim() : null,
      product: src.product ? String(src.product).trim() : null,
      combo:   src.combo   ? String(src.combo).trim()   : null,
    };
    delete payload.clientEmail;
    if (!payload.dateFrom) payload.dateFrom = null;
    if (!payload.dateTo)   payload.dateTo   = null;

    const r = this.state.client.pushData(API_URL_DIRECT, payload); // jQuery sync call
    if (!this.isErrorLike(r)) {
      const next = { ...this.state.item };
      if (r.iref) next.iref = r.iref;
      if (r.qrCode) next.qrCode = r.qrCode; // not shown
      this.setState({
        item: next,
        somethingHasChanged: false,
        isSaving: false,
        edit: false,
      });
      swal('Saved', 'Direct promotion saved.', 'success', { buttons: false, timer: 1100 });
      clearPromotionListCache();
      if (typeof this.props.itemUpdated === 'function') this.props.itemUpdated();
    } else {
      const msg = this.normalizeErrors(r);
      this.setState({ isSaving: false });
      swal('Save failed', msg, 'error');
    }
  };

  safeImageSrc = (raw) => {
    let s = raw && typeof raw === 'object' ? (raw.data || raw.url || raw.src || '') : (raw || '');
    if (typeof s !== 'string') s = '';
    s = s.replace(/\r?\n/g, '').trim();
    if (!s) return noImage;
    if (s.startsWith('data:image') && s.includes(';base64,')) return s;
    if (/^https?:\/\//i.test(s) || s.startsWith('/') || s.startsWith('./') || s.startsWith('../')) return s;
    return noImage;
  };

  render() {
    const i = this.state.item || {};
    const isEdit = !!this.state.edit;
    const bothSelected = !!((i?.product) && (i?.combo));
    const errs = this.getDateErrors();
    const title = i.name || (i.iref ? `Promotion #${i.iref}` : 'New Direct Promotion');
    const length = (i.name || '').length;
    const priceWarningLabel = i.product_price_warning
      ? 'Product not available for active locations'
      : i.combo_price_warning
        ? 'Combo not available for active locations'
        : '';

    const statusChip = (
      <Chip size="small" label={this.statusName(i.status)} sx={{ ml: 0.75 }} />
    );

    const productOptions = this.state.productObj &&
      !this.state.productOpts.some(o => String(o.iref) === String(this.state.productObj.iref))
        ? [this.state.productObj, ...this.state.productOpts]
        : this.state.productOpts;
    const comboOptions = this.state.comboObj &&
      !this.state.comboOpts.some(o => String(o.iref) === String(this.state.comboObj.iref))
        ? [this.state.comboObj, ...this.state.comboOpts]
        : this.state.comboOpts;

    return (
      <Card sx={{ borderRadius: '14px', m: 1 }}>
        <CardHeader
          avatar={          
            <Box sx={{ display: 'flex', flexDirection: 'row', width: "100%" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%" }}>      
                <Avatar sx={{ width: 28, height: 28 }}><ImageIcon fontSize="small" />
                </Avatar>  
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%" }}>
                {i.iref ? (
                  <Typography variant="subtitle1" color="text.secondary">
                    {i.iref}
                  </Typography>
                ) : null}
              </Box>
            </Box> 
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {this.state.edit && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon fontSize="small" />}
                  disabled={
                    this.state.isSaving ||
                    bothSelected ||
                    (!i.product && !i.combo) ||
                    !this.hasValidDates() ||
                    !this.state.somethingHasChanged
                  }
                  onClick={this.save}
                >
                  {this.state.isSaving ? 'Saving…' : 'Save'}
                </Button>
              )}
              <IconButton
                aria-label="edit"
                onClick={this.handleEditClick}
                size="small"
              >
                <EditIcon
                  sx={{ color: this.state.edit ? '#8f241d' : '#9aa0a6' }}
                  fontSize="small"
                />
              </IconButton>
            </Box>
          }
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <span>{title}</span>
              {statusChip}

              {i.has_price_list_warning ? (
                <Chip
                  size="small"
                  icon={<WarningAmberIcon />}
                  color="warning"
                  variant="outlined"
                  label={priceWarningLabel}
                />
              ) : null}
            </Box>
          }
          titleTypographyProps={{
            component: 'div',
            variant: 'subtitle1',
            sx: { fontWeight: 600, lineHeight: 1.2 }
          }}
          sx={{ py: 1, px: 1.25, ...(isEdit && { '& .MuiCardHeader-action': { alignSelf: 'center', m: 0 } }) }}
        />

        {/* ALWAYS VISIBLE CONTENT (no Collapse) */}
        <CardContent sx={{ pt: 0.5, pb: 1, px: 1.25 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isEdit ? 'repeat(12, minmax(0, 1fr))' : { xs: '1fr', md: 'repeat(12, minmax(0, 1fr))' },
              columnGap: isEdit ? 2 : 0.75,
              rowGap: isEdit ? 1.5 : 0.75,
              alignItems: isEdit ? 'start' : 'center',
              px: { xs: 0.5, sm: 1.5, md: 2 },
              maxWidth: isEdit ? 1200 : 'none',
              '& .MuiFormControl-root': { m: 0 }
            }}
          >
            {/* Row 1 — Name and Status — only in EDIT.
                In READ-ONLY these are already shown in the header, so we skip to avoid duplicates. */}
            {isEdit && (
              <>
                <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 8', md: 'span 6' }, maxWidth: { md: 520 } }}>
                  <TextField
                    id="name"
                    label="Name"
                    {...this.compactTF}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={i.name || ''}
                    onChange={this.changeValue}
                    inputProps={{ maxLength: 40 }}
                    helperText={
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          textAlign: 'right',
                          width: '100%',
                          color: length > 32 ? 'warning.main' : 'text.secondary'
                        }}
                      >
                        {length} / 40
                      </Box>
                    }
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 4', md: 'span 2' }, maxWidth: { md: 200 } }}>
                  <DropDown
                    name="Status"
                    value={String(i.status ?? 0)}
                    doptions={Object.fromEntries(Object.entries(this.CLAIM_STATUS).map(([k, v]) => [String(k), v]))}
                    onChangeFunction={(v) =>
                      this.setState((s) => ({
                        item: { ...s.item, status: parseInt(v, 10) },
                        somethingHasChanged: true,
                      }))
                    }
                    minWidth={0}
                    size="small"
                  />
                </Box>
              </>
            )}

            {/* Client lookup, selected client, and validity stay together. */}
            <Box sx={{
              gridColumn: isEdit ? { xs: '1 / -1', sm: 'span 6', md: '1 / span 3' } : { xs: '1 / -1', md: 'span 6' },
              maxWidth: isEdit ? { md: 360 } : undefined,
            }}>
              {isEdit ? (
                <TextField
                  id="clientQuery"
                  label="Client (email or phone)"
                  {...this.compactTF}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={this.state.clientQuery || ''}
                  onChange={(e) => this.setState({ clientQuery: e.target.value })}
                  placeholder="e.g. john@site.com or +4074…"
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">Client email</Typography>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{i.clientEmail || '—'}</Typography>
                </Box>
              )}
            </Box>

            {isEdit && (
              <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3', md: 'span 2' }, maxWidth: { md: 140 }, justifySelf: { md: 'end' } }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={this.findClientByContact}
                  fullWidth
                  sx={{ minHeight: 40, whiteSpace: 'nowrap' }}
                >
                  Find client
                </Button>
              </Box>
            )}

            <Box sx={{
              gridColumn: isEdit ? { xs: 'span 6', sm: 'span 3', md: 'span 2' } : { xs: '1 / -1', md: 'span 2' },
              maxWidth: isEdit ? { md: 150 } : undefined,
              px: 0,
            }}>
              {isEdit ? (
                <Box aria-live="polite" sx={{ minHeight: 40, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="caption" color="text.secondary">Selected client ID</Typography>
                  <Chip size="small" variant="outlined" label={i.client || '—'} sx={{ maxWidth: '100%' }} />
                </Box>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">Client ID</Typography>
                  <Typography variant="body2">{i.client || '—'}</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ gridColumn: isEdit ? { xs: '1 / -1', sm: 'span 6', md: 'span 2' } : { xs: '1 / -1', md: 'span 2' }, maxWidth: isEdit ? { md: 240 } : undefined }}>
              {isEdit ? (
                <TextField
                  id="dateFrom_ui"
                  label="From"
                  type="datetime-local"
                  {...this.compactTF}
                  variant="outlined"
                  fullWidth
                  inputProps={{ step: 60 }}
                  value={this.toDateTimeInput(i.dateFrom, false)}
                  onChange={(e) => this.changeSchedule('dateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={errs.from || errs.range}
                  helperText={errs.from ? 'Required' : errs.range ? 'From must be ≤ To' : 'Europe/Bucharest'}
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">From</Typography>
                  <Typography variant="body2">{this.scheduleLabel(i.dateFrom, false)}</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ gridColumn: isEdit ? { xs: '1 / -1', sm: 'span 6', md: 'span 2' } : { xs: '1 / -1', md: 'span 2' }, maxWidth: isEdit ? { md: 240 } : undefined }}>
              {isEdit ? (
                <TextField
                  id="dateTo_ui"
                  label="To"
                  type="datetime-local"
                  {...this.compactTF}
                  variant="outlined"
                  fullWidth
                  inputProps={{ step: 60 }}
                  value={this.toDateTimeInput(i.dateTo, true)}
                  onChange={(e) => this.changeSchedule('dateTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={errs.to || errs.range}
                  helperText={errs.to ? 'Required' : errs.range ? 'From must be ≤ To' : 'Europe/Bucharest'}
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">To</Typography>
                  <Typography variant="body2">{this.scheduleLabel(i.dateTo, true)}</Typography>
                </Box>
              )}
            </Box>

            {/* Row 3 — Product (6) | Combo (6) */}
            <Box sx={{ gridColumn: isEdit ? { xs: '1 / -1', sm: 'span 6' } : { xs: '1 / -1', md: 'span 6' }, maxWidth: isEdit ? { md: 480 } : undefined }}>
              {isEdit ? (
                <Autocomplete
                  options={productOptions}
                  disabled={!!i.combo && !i.product}
                  loading={this.state.productSearching}
                  onInputChange={this.onProductInput}
                  filterOptions={(x) => x}
                  openOnFocus
                  size="small"
                  value={this.state.productObj}
                  isOptionEqualToValue={(a, b) => String(a?.iref) === String(b?.iref)}
                  onChange={(_, val) =>
                    this.setState((s) => ({
                      item: { ...s.item, product: val ? val.iref : null },
                      productObj: val || null,
                      somethingHasChanged: true,
                    }))
                  }
                  getOptionLabel={(opt) => opt?.name || ''}
                  renderInput={(params) => <TextField {...params} label="Product" {...this.compactTF} fullWidth />}
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">Product</Typography>
                  <Typography variant="body2">{this.state.productObj?.name || '—'}</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ gridColumn: isEdit ? { xs: '1 / -1', sm: 'span 6' } : { xs: '1 / -1', md: 'span 6' }, maxWidth: isEdit ? { md: 480 } : undefined }}>
              {isEdit ? (
                <Autocomplete
                  options={comboOptions}
                  disabled={!!i.product && !i.combo}
                  loading={this.state.comboSearching}
                  onInputChange={this.onComboInput}
                  filterOptions={(x) => x}
                  openOnFocus
                  size="small"
                  value={this.state.comboObj}
                  isOptionEqualToValue={(a, b) => String(a?.iref) === String(b?.iref)}
                  onChange={(_, val) =>
                    this.setState((s) => ({
                      item: { ...s.item, combo: val ? val.iref : null },
                      comboObj: val || null,
                      somethingHasChanged: true,
                    }))
                  }
                  getOptionLabel={(opt) => opt?.name || ''}
                  renderInput={(params) => <TextField {...params} label="Combo" {...this.compactTF} fullWidth />}
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">Combo</Typography>
                  <Typography variant="body2">{this.state.comboObj?.name || '—'}</Typography>
                </Box>
              )}
            </Box>
            {isEdit && (
              <Typography
                variant="caption"
                role="status"
                sx={{ gridColumn: '1 / -1', mt: -0.5, color: bothSelected ? 'warning.main' : 'text.secondary' }}
              >
                {bothSelected
                  ? 'Select only one: clear Product or Combo before saving.'
                  : i.product
                    ? 'Combo is unavailable while a Product is selected. Clear Product to choose a Combo.'
                    : i.combo
                      ? 'Product is unavailable while a Combo is selected. Clear Combo to choose a Product.'
                      : 'Select a Product or Combo.'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }
}
